import React, { FC, MouseEventHandler, useCallback, useRef } from 'react';
import { Box, Fade } from '@chakra-ui/react';

interface Props {
    /** True only if the photo has the black and white filter applied */
    isBlackAndWhite: boolean;
    /** True while the pointer/focus has rested on the tile long enough to preview */
    isPreviewActive: boolean;
    /** Thumbnail shown until the video's first frame decodes */
    posterUrl: string;
    /** Progressive MP4 URL to preview */
    previewUrl: string;
}

const handleContextMenu: MouseEventHandler = (event) => event.preventDefault();

// Matches the 0.25s opacity transition PhotoActionList/PhotoOverlay use for their hover fades.
const FADE_TRANSITION = { enter: { duration: 0.25 }, exit: { duration: 0.25 } };

const FADE_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
} as const;

/**
 * A silent, looping video preview that fades in over a grid item's thumbnail. Mounted only
 * while the preview is active, so an unhovered gallery never requests any video.
 */
const VideoGridItemPreview: FC<Props> = ({
    isBlackAndWhite,
    isPreviewActive,
    posterUrl,
    previewUrl
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // A callback ref, not useEffect(..., [previewUrl]) — Fade's unmountOnExit mounts and
    // unmounts this <video> on every hover in/out while previewUrl itself never changes, so
    // an effect keyed on previewUrl only ever ran once, before Fade had rendered the node.
    const attachVideoRef = useCallback((video: HTMLVideoElement | null) => {
        if (video) {
            videoRef.current = video;

            // React can drop the muted attribute on mount, and an unmuted video is not
            // allowed to autoplay, so set the property directly before asking it to play.
            video.muted = true;

            // Promise.resolve absorbs both a browser that blocks autoplay and jsdom's void play().
            void Promise.resolve(video.play()).catch(() => undefined);

            return;
        }

        const previousVideo = videoRef.current;

        videoRef.current = null;

        if (previousVideo) {
            previousVideo.pause();
            // Dropping the source aborts any in-flight range request for the abandoned preview.
            previousVideo.removeAttribute('src');
            previousVideo.load();
        }
    }, []);

    return (
        <Fade in={isPreviewActive} unmountOnExit transition={FADE_TRANSITION} style={FADE_STYLE}>
            {/* Fills any letterbox gap so the thumbnail doesn't show through it. */}
            <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                bg="background"
                pointerEvents="none"
            >
                <video
                    ref={attachVideoRef}
                    data-testid="videoPreview"
                    src={previewUrl}
                    poster={posterUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    controls={false}
                    disablePictureInPicture
                    draggable={false}
                    // Decorative: the thumbnail's alt text already names the video, and the
                    // tile's controls live in PhotoOverlay, so the preview is hidden from
                    // assistive tech and from tab order.
                    aria-hidden="true"
                    tabIndex={-1}
                    onContextMenu={handleContextMenu}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        filter: isBlackAndWhite ? 'grayscale(1)' : undefined,
                        // Matches the thumbnail, so clicks fall through to PhotoOverlay.
                        pointerEvents: 'none'
                    }}
                />
            </Box>
        </Fade>
    );
};

export default VideoGridItemPreview;
